import dayjs from 'dayjs';
import {
  buildMetricChartSeriesFromBuckets,
  buildMetricSummaryFromBuckets,
  createDailyExecutionMetricBucket,
  type DailyExecutionMetricBucket,
} from '@/features/shared/utils/chartBucketSeries';
import { resolveChartDates } from '@/features/shared/utils/chartDateWindow';
import type { PipelineStatusKey } from '@/features/quality/components/qualityStatusDataHelpers';
import type { BuildDto, CiDisplayStatusKey, PipelineRunSummaryDto } from '@flowqa/shared';

type LineChartDatum = { [key: string]: number | string | null; label: string };

type PipelineMiniMetricChartData = {
  passRateChartData: LineChartDatum[];
  frequencyChartData: LineChartDatum[];
  durationChartData: LineChartDatum[];
  flakeScoreChartData: LineChartDatum[];
  passRate: number | null;
  frequencyPerDay: number | null;
  avgDurationSeconds: number | null;
  flakeScore: number | null;
};

type DayBucket = DailyExecutionMetricBucket;

const resolveBuildTimestamp = (build: BuildDto): Date | null =>
  build.finishedAt ?? build.startedAt ?? build.createdAt;

const resolveBuildDurationSeconds = (build: BuildDto): number | null => {
  if (build.startedAt === null || build.finishedAt === null) return null;
  const durationMs = build.finishedAt.getTime() - build.startedAt.getTime();
  return Number.isFinite(durationMs) && durationMs >= 0 ? durationMs / 1000 : null;
};

const createDayBucket = (): DayBucket => createDailyExecutionMetricBucket();

const resolveBuildDisplayStatus = ({
  status,
  lifecycleState,
  conclusion,
}: BuildDto): CiDisplayStatusKey => {
  if (lifecycleState === 'running') return 'running';
  if (conclusion === 'action_required') return 'action_required';
  if (lifecycleState === 'queued' || lifecycleState === 'waiting') return 'queued';
  if (conclusion != null) return conclusion;
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'failed';
  if (status === 'running') return 'running';
  return 'queued';
};

export const buildIsoChartDates = (startIso: string, endIso: string): string[] => {
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
    return [];
  }

  const dates: string[] = [];
  const cursor = new Date(start);
  cursor.setUTCHours(0, 0, 0, 0);
  const endDay = new Date(end);
  endDay.setUTCHours(0, 0, 0, 0);

  while (cursor <= endDay) {
    dates.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return dates;
};

export const resolveBuildChartTimestamp = (build: BuildDto): Date | null =>
  build.finishedAt ?? build.startedAt ?? build.createdAt;

export const isBuildWithinSelectedWindow = (
  build: BuildDto,
  startIso: string | null,
  endIso: string | null,
): boolean => {
  const timestamp = resolveBuildChartTimestamp(build);
  if (timestamp === null || Number.isNaN(timestamp.getTime())) {
    return false;
  }
  if (startIso !== null) {
    const start = new Date(startIso);
    if (!Number.isNaN(start.getTime()) && timestamp < start) {
      return false;
    }
  }
  if (endIso !== null) {
    const end = new Date(endIso);
    if (!Number.isNaN(end.getTime()) && timestamp > end) {
      return false;
    }
  }
  return true;
};

export const buildPreviousCustomWindow = (
  startDay: string | null,
  endDay: string | null,
): { startIso: string | null; endIso: string | null } => {
  const rawStart = dayjs(startDay ?? endDay ?? null);
  const rawEnd = dayjs(endDay ?? startDay ?? null);
  if (!rawStart.isValid() || !rawEnd.isValid()) {
    return { startIso: null, endIso: null };
  }
  const start = rawStart.isAfter(rawEnd) ? rawEnd.startOf('day') : rawStart.startOf('day');
  const end = rawStart.isAfter(rawEnd) ? rawStart.endOf('day') : rawEnd.endOf('day');
  const spanDays = end.startOf('day').diff(start.startOf('day'), 'day') + 1;
  const previousEnd = start.subtract(1, 'day').endOf('day');
  const previousStart = previousEnd.subtract(spanDays - 1, 'day').startOf('day');
  return {
    startIso: previousStart.toISOString(),
    endIso: previousEnd.toISOString(),
  };
};

export const buildCountsFromBuilds = (
  builds: BuildDto[] | null,
): Partial<Record<PipelineStatusKey, number>> => {
  if (!builds) {
    return { queued: 0, running: 0, success: 0, warning: 0, failed: 0, none: 0 };
  }
  return builds.reduce<Partial<Record<PipelineStatusKey, number>>>(
    (acc, build) => {
      const status = resolveBuildDisplayStatus(build);
      acc[status] = (acc[status] ?? 0) + 1;
      acc.none = 0;
      return acc;
    },
    { queued: 0, running: 0, success: 0, warning: 0, failed: 0, none: 0 },
  );
};

export const calculateBuildPassRate = (
  counts: Partial<Record<PipelineStatusKey, number>>,
): number | null => {
  const completed = (counts.success ?? 0) + (counts.warning ?? 0);
  const failed = (counts.failed ?? 0) + (counts.canceled ?? 0) + (counts.timed_out ?? 0);
  const denominator = completed + failed;
  if (denominator <= 0) {
    return null;
  }
  return (completed / denominator) * 100;
};

export const buildPipelineMiniMetricChartDataFromBuilds = (
  builds: BuildDto[],
  chartDates: string[],
): PipelineMiniMetricChartData => {
  const buckets = new Map<string, DayBucket>();
  let completed = 0;
  let failed = 0;
  let durationSumSeconds = 0;
  let durationCount = 0;

  [...builds]
    .sort((left, right) => {
      const leftTimestamp = resolveBuildTimestamp(left);
      const rightTimestamp = resolveBuildTimestamp(right);
      const timestampDelta = (leftTimestamp?.getTime() ?? 0) - (rightTimestamp?.getTime() ?? 0);
      return timestampDelta === 0 ? left.id.localeCompare(right.id) : timestampDelta;
    })
    .forEach((build) => {
      const timestamp = resolveBuildTimestamp(build);
      if (timestamp === null || Number.isNaN(timestamp.getTime())) {
        return;
      }

      const dayKey = timestamp.toISOString().slice(0, 10);
      const bucket = buckets.get(dayKey) ?? createDayBucket();
      bucket.total += 1;
      const displayStatus = resolveBuildDisplayStatus(build);
      if (displayStatus === 'success' || displayStatus === 'warning') {
        bucket.completed += 1;
        completed += 1;
      }
      if (
        displayStatus === 'failed' ||
        displayStatus === 'canceled' ||
        displayStatus === 'timed_out'
      ) {
        bucket.failed += 1;
        failed += 1;
      }
      bucket.flakeOutcomes.push(
        displayStatus === 'success' || displayStatus === 'warning' ? 'passed' : 'notPassed',
      );

      const durationSeconds = resolveBuildDurationSeconds(build);
      if (durationSeconds !== null) {
        bucket.durationSumSeconds += durationSeconds;
        bucket.durationCount += 1;
        durationSumSeconds += durationSeconds;
        durationCount += 1;
      }

      buckets.set(dayKey, bucket);
    });

  const dates = chartDates.length > 0 ? chartDates : Array.from(buckets.keys()).sort();
  const { passRateChartData, frequencyChartData, durationChartData, flakeScoreChartData } =
    buildMetricChartSeriesFromBuckets(buckets, dates);
  const summary = buildMetricSummaryFromBuckets(buckets, dates);

  const passRateDenominator = completed + failed;

  return {
    passRateChartData: passRateChartData as LineChartDatum[],
    frequencyChartData: frequencyChartData as LineChartDatum[],
    durationChartData: durationChartData as LineChartDatum[],
    flakeScoreChartData: flakeScoreChartData as LineChartDatum[],
    passRate: passRateDenominator > 0 ? (completed / passRateDenominator) * 100 : null,
    frequencyPerDay: builds.length > 0 && dates.length > 0 ? builds.length / dates.length : null,
    avgDurationSeconds: durationCount > 0 ? durationSumSeconds / durationCount : null,
    flakeScore: summary.flakeScore,
  };
};

export const buildPipelineMiniMetricChartDataFromSummary = (
  summary: PipelineRunSummaryDto | null,
  chartDates: string[] = [],
): PipelineMiniMetricChartData => {
  const buckets = summary?.buckets ?? [];
  const bucketsByDay = new Map(
    buckets.map((bucket) => [bucket.day.toISOString().slice(0, 10), bucket] as const),
  );
  const dates = resolveChartDates(
    chartDates,
    buckets.map((bucket) => bucket.day.toISOString().slice(0, 10)),
  );
  const metricBucketsByDay = new Map(
    buckets.map((bucket) => {
      const metricBucket: DayBucket = {
        total: bucket.total,
        completed: bucket.displayStatusCounts.success + bucket.displayStatusCounts.warning,
        failed:
          bucket.displayStatusCounts.failed +
          bucket.displayStatusCounts.canceled +
          bucket.displayStatusCounts.timed_out,
        durationSumSeconds: bucket.averageDurationMs != null ? bucket.averageDurationMs / 1000 : 0,
        durationCount: bucket.averageDurationMs != null ? 1 : 0,
        flakeOutcomes: [],
        flakeScore: bucket.flakeScore,
      };
      return [bucket.day.toISOString().slice(0, 10), metricBucket] as const;
    }),
  );
  const {
    passRateChartData: defaultPassRateChartData,
    frequencyChartData,
    durationChartData,
    flakeScoreChartData,
  } = buildMetricChartSeriesFromBuckets(metricBucketsByDay, dates);
  return {
    passRateChartData: defaultPassRateChartData.map((row) => ({
      ...row,
      passRate: bucketsByDay.get(String(row.label))?.passRate ?? row.passRate,
    })) as unknown as LineChartDatum[],
    frequencyChartData: frequencyChartData as LineChartDatum[],
    durationChartData: durationChartData as LineChartDatum[],
    flakeScoreChartData: flakeScoreChartData as LineChartDatum[],
    passRate: summary?.averageExecutionPassRate ?? null,
    frequencyPerDay: summary?.averageExecutionFrequencyPerDay ?? null,
    avgDurationSeconds:
      summary?.averageExecutionDurationMs != null
        ? summary.averageExecutionDurationMs / 1000
        : null,
    flakeScore: summary?.averageExecutionFlakeScore ?? null,
  };
};
